import { cn } from '@bridebook/aura';
import { IWedding_Website_Section_PhotosField } from '@bridebook/models/source/models/Weddings.types';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import { ImageX } from '@bridebook/ui';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { useSelector } from '@bridebook/web/lib/utils';
import { isPrimaryPhotoSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import { CSSProperties, useMemo } from 'react';
import { useWeddingWebsiteData } from '../../data/data-context';
import { getPhotoOrientation } from '../../sections/photo-orientation';
import { FloatingEditButton } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/floating-edit-button';

type Props = {
  /**
   * The index of the photo in the photos array
   */
  index: number;
};
export const Photo = ({ index: id }: Props) => {
  const { website } = useWeddingWebsiteData();
  const primaryPhotoSection = website.sections.find(isPrimaryPhotoSection);
  if (!primaryPhotoSection) return null;
  const photo = primaryPhotoSection.data[id];
  if (!photo) return null;

  return <PhotoDisplay photo={photo} />;
};

export const ASSET_PREFIX = `${IMAGES_BASE_URL}/wedding-websites/uploads`;
// Fixed sizes comming from design
const LANDSCAPE_DESKTOP_WIDTH = 640;
const PORTRAIT_DESKTOP_WIDTH = 440;
const LANDSCAPE_MOBILE_WIDTH = 587;
const PORTRAIT_MOBILE_WIDTH = PORTRAIT_DESKTOP_WIDTH;

const PhotoDisplay = ({
  photo,
}: {
  photo: IWedding_Website_Section_PhotosField['data'][number];
}) => {
  const { isDesktop } = useSelector(getScreenType);

  const photoDimensions = useMemo(
    () => getPhotoOrientation({ width: photo.width, height: photo.height }),
    [photo.width, photo.height],
  );

  return (
    <div
      className={cn(
        photoDimensions.orientation === 'landscape' ? 'px-0' : 'px-12',
        'flex w-full justify-center @screen-lg:px-12',
      )}
      style={
        {
          '--landscape-desktop-width': `${LANDSCAPE_DESKTOP_WIDTH}px`,
          '--portrait-desktop-width': `${PORTRAIT_DESKTOP_WIDTH}px`,
          '--landscape-mobile-width': `${LANDSCAPE_MOBILE_WIDTH}px`,
          '--portrait-mobile-width': `${PORTRAIT_MOBILE_WIDTH}px`,
        } as CSSProperties
      }>
      <div
        className={cn(
          'group relative z-20 w-full',
          photoDimensions.orientation === 'landscape'
            ? 'max-w-[var(--landscape-mobile-width)] @screen-lg:max-w-[var(--landscape-desktop-width)]'
            : 'max-w-[var(--portrait-mobile-width)] @screen-lg:max-w-[var(--portrait-desktop-width)]',
        )}
        style={{ aspectRatio: photoDimensions.aspectRatioString }}>
        {!isDesktop ? (
          <ImageX
            src={`${IMAGES_BASE_URL}/${photo.path}`}
            w={photoDimensions.mobileWidth}
            h={photoDimensions.mobileHeight}
            fit="cover"
            customParams={{ gravity: 'face' }}
            classNames={{
              wrapper: 'absolute',
            }}
          />
        ) : (
          <ImageX
            src={`${IMAGES_BASE_URL}/${photo.path}`}
            w={photoDimensions.desktopWidth}
            h={photoDimensions.desktopHeight}
            fit="cover"
            customParams={{ gravity: 'face' }}
            classNames={{
              wrapper: 'absolute',
            }}
          />
        )}
        <FloatingEditButton detailsSectionId="photos" />
      </div>
    </div>
  );
};
